import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { adminApi } from '../../api';
import type {
  ListUsersResult,
  UserWithStats,
  UserRole,
  UserStatus,
} from '../../types';
import {
  ROLE_DISPLAY_NAMES,
  STATUS_DISPLAY_NAMES,
  isAdmin,
  isSuperAdmin,
  canManageUser,
} from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';
import { AdminBreadcrumbs } from '../../components/admin';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function RoleChip({ role }: { role?: UserRole }) {
  const colors: Record<UserRole, 'error' | 'warning' | 'info' | 'default'> = {
    super_admin: 'error',
    admin: 'warning',
    support: 'info',
    user: 'default',
  };

  return (
    <Chip
      label={ROLE_DISPLAY_NAMES[role || 'user']}
      color={colors[role || 'user']}
      size="small"
    />
  );
}

function StatusChip({ status }: { status?: UserStatus }) {
  return (
    <Chip
      label={STATUS_DISPLAY_NAMES[status || 'active']}
      color={status === 'disabled' ? 'error' : 'success'}
      size="small"
    />
  );
}

export function UsersManagementPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  useDocumentTitle('Users Management');

  const [result, setResult] = useState<ListUsersResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>(
    (searchParams.get('role') as UserRole) || ''
  );
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>(
    (searchParams.get('status') as UserStatus) || ''
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => getStoredRowsPerPage());

  // Dialog state
  const [selectedUser, setSelectedUser] = useState<UserWithStats | null>(null);
  const [dialogType, setDialogType] = useState<'role' | 'disable' | 'enable' | null>(null);
  const [newRole, setNewRole] = useState<UserRole>('user');
  const [disableReason, setDisableReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.listUsers({
        search: search || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        page: page + 1,
        pageSize,
      });
      setResult(data);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter, page, pageSize]);

  useEffect(() => {
    if (!isAdmin(currentUser)) {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [currentUser, navigate, fetchUsers]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleRoleFilterChange = (value: UserRole | '') => {
    setRoleFilter(value);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('role', value);
    } else {
      params.delete('role');
    }
    setSearchParams(params);
  };

  const handleStatusFilterChange = (value: UserStatus | '') => {
    setStatusFilter(value);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('status', value);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const handleChangeRole = async () => {
    if (!selectedUser) return;
    try {
      setActionLoading(true);
      await adminApi.updateUserRole(selectedUser.id, newRole);
      setDialogType(null);
      setSelectedUser(null);
      fetchUsers();
    } catch (_err) {
      setError('Failed to update role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisableUser = async () => {
    if (!selectedUser) return;
    try {
      setActionLoading(true);
      await adminApi.disableUser(selectedUser.id, disableReason);
      setDialogType(null);
      setSelectedUser(null);
      setDisableReason('');
      fetchUsers();
    } catch (_err) {
      setError('Failed to disable user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnableUser = async () => {
    if (!selectedUser) return;
    try {
      setActionLoading(true);
      await adminApi.enableUser(selectedUser.id);
      setDialogType(null);
      setSelectedUser(null);
      fetchUsers();
    } catch (_err) {
      setError('Failed to enable user');
    } finally {
      setActionLoading(false);
    }
  };

  const canEditUser = (targetUser: UserWithStats) => {
    if (!currentUser) return false;
    if (currentUser.id === targetUser.id) return false;
    return canManageUser(currentUser, targetUser);
  };

  if (loading && !result) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <AdminBreadcrumbs items={[{ label: 'Users' }]} />
      <Typography variant="h4" sx={{ mb: 3 }}>
        User Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search"
              placeholder="Email or name..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            />
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => handleRoleFilterChange(e.target.value as UserRole | '')}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="support">Support</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => handleStatusFilterChange(e.target.value as UserStatus | '')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Boards</TableCell>
                <TableCell align="right">Squares</TableCell>
                <TableCell align="right">Spent</TableCell>
                <TableCell align="right">Earned</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result?.users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {user.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <RoleChip role={user.role} />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={user.status} />
                  </TableCell>
                  <TableCell align="right">{user.boardCount}</TableCell>
                  <TableCell align="right">{user.squareCount}</TableCell>
                  <TableCell align="right">{formatCurrency(user.totalSpent)}</TableCell>
                  <TableCell align="right">{formatCurrency(user.totalEarned)}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEditUser(user) && (
                        <>
                          <Tooltip title="Change Role">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedUser(user);
                                setNewRole(user.role || 'user');
                                setDialogType('role');
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {user.status === 'active' ? (
                            <Tooltip title="Disable User">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDialogType('disable');
                                }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Enable User">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDialogType('enable');
                                }}
                              >
                                <EnableIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {result && (
          <TablePagination
            component="div"
            count={result.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPageSize(newSize);
              setStoredRowsPerPage(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Card>

      {/* Change Role Dialog */}
      <Dialog open={dialogType === 'role'} onClose={() => setDialogType(null)}>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Change role for <strong>{selectedUser?.displayName}</strong>
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={newRole}
              label="Role"
              onChange={(e) => setNewRole(e.target.value as UserRole)}
            >
              {isSuperAdmin(currentUser) && (
                <MenuItem value="admin">Admin</MenuItem>
              )}
              <MenuItem value="support">Support</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>Cancel</Button>
          <Button onClick={handleChangeRole} disabled={actionLoading} variant="contained">
            {actionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable User Dialog */}
      <Dialog open={dialogType === 'disable'} onClose={() => setDialogType(null)}>
        <DialogTitle>Disable User Account</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to disable the account for{' '}
            <strong>{selectedUser?.displayName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will prevent them from logging in and hide all their boards from other users.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={disableReason}
            onChange={(e) => setDisableReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>Cancel</Button>
          <Button
            onClick={handleDisableUser}
            disabled={actionLoading}
            variant="contained"
            color="error"
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Disable Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enable User Dialog */}
      <Dialog open={dialogType === 'enable'} onClose={() => setDialogType(null)}>
        <DialogTitle>Enable User Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to re-enable the account for{' '}
            <strong>{selectedUser?.displayName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>Cancel</Button>
          <Button
            onClick={handleEnableUser}
            disabled={actionLoading}
            variant="contained"
            color="success"
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Enable Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
